<template>
  <div class="correlation">
    <vue-c3
      class="chart"
      :handler="handler">
    </vue-c3>
  </div>
</template>

<script>
import Vue from 'vue';
import VueC3 from 'vue-c3';
import dayjs from 'dayjs';
import {mapGetters} from 'vuex';

require('c3/c3.css');

export default {
  name: 'ChartMultipleCorrelation',
  components: {
    VueC3
  },
  props: {
    pairs: {
      type: Array,
      default: () => []
    },
    data: {
      type: Object,
      default: () => {}
    },
    width: {
      type: Number,
      default: 0
    },
    height: {
      type: Number,
      default: 0
    },
    grid: {
      type: Boolean,
      default: false
    },
    range: {
      type: Object,
      default: () => ({ x: [], y: [] })
    }
  },
  data () {
    return {
      handler: new Vue(),
      xs: {},
      columns: [],
      tooltip: {},
      labelX: '',
      labelY: ''
    };
  },
  watch: {
    data: {
      handler () {
        this.onDataChanged();
      },
      deep: true
    },
    grid: {
      handler () {
        this.draw();
      }
    },
    range: {
      handler () {
        this.onDataChanged();
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters([
      'getTagsTree'
    ])
  },
  methods: {
    getFilteredTags (tagArray) {
      let filteredTags = [];
      const flattenTags = this.flatten(this.getTagsTree.slice());
      tagArray.forEach(tagId => {
        if (this.getTagsTree !== undefined) {
          filteredTags.push(flattenTags.find(tag => tag.data.id === Number(tagId)).text);
        }
      });
      return filteredTags.join(',');
    },
    flatten (data) {
      return data.reduce((r, {children, ...rest}) => {
        r.push(rest);
        if (children) r.push(...this.flatten(children));
        return r;
      }, []);
    },
    onDataChanged () {
      this.filterData(this.data);
      this.draw();
    },
    filterData (data) {
      this.xs = [];
      this.columns = [];
      this.tooltip = [];

      for (let pair = 0; pair < this.pairs.length; pair++) {
        const x = this.pairs[pair].x;
        const y = this.pairs[pair].y;

        const xDataArray = [];
        const yDataArray = [];

        xDataArray.push(this.pairs[pair].label + '_x');
        yDataArray.push(this.pairs[pair].label);

        const tooltip = {
          datetimes: [],
          x: this.getFilteredTags(this.$route.query.filterTagsX.split(',')),
          y: this.getFilteredTags(this.$route.query.filterTagsY.split(','))
        };

        if (data && data.variables) {
          let xVariableData = null;
          let yVariableData = null;

          for (let i = 0; i < data.variables.length; i++) {
            if (!xVariableData && data.variables[i].metadata.id === x) {
              xVariableData = data.variables[i].data.slice();
              this.labelX = this.getFilteredTags(this.$route.query.filterTagsX.split(','));
            }
            if (!yVariableData && data.variables[i].metadata.id === y) {
              yVariableData = data.variables[i].data.slice();
              this.labelY = this.getFilteredTags(this.$route.query.filterTagsY.split(','));
            }
            if (xVariableData && yVariableData) {
              // Match by timestamp
              for (let x = 0; x < xVariableData.length; x++) {
                // Change 'find' to 'for' to improve the performance
                // const yData = yVariableData.find(element => element.timestamp === xVariableData[x].timestamp);
                let yData;
                for (let y = 0; y < yVariableData.length; y++) {
                  if (yVariableData[y].timestamp === xVariableData[x].timestamp) {
                    yData = yVariableData[y];
                    break;
                  }
                }

                if (yData) {
                  const valueX = Number(xVariableData[x].value);
                  const valueY = Number(yData.value);

                  filterByRange(this.range, valueX, valueY, xDataArray, yDataArray);
                  tooltip.datetimes.push(xVariableData[x].timestamp);
                }
              }
            }
          }

          this.columns.push(yDataArray);
          this.columns.push(xDataArray);
          this.xs[yDataArray[0]] = xDataArray[0];
          this.tooltip[yDataArray[0]] = tooltip;
        }
      }
    },
    draw () {
      const context = this;
      const options = {
        size: {
          height: this.height,
          width: this.width
        },
        data: {
          xSort: false,
          xs: this.xs,
          columns: this.columns,
          type: 'scatter'
        },
        axis: {
          x: {
            label: this.labelX,
            tick: {
              fit: false
            }
          },
          y: {
            label: this.labelY
          }
        },
        grid: {
          y: {
            show: this.grid
          }
        },
        zoom: {
          enabled: true
        },
        legend: {
          show: true
        },
        tooltip: {
          contents: function (d) {
            const id = d[0].id;
            const index = d[0].index;
            const date = context.tooltip[id].datetimes[index];
            const title = dayjs(date * 1000).format('YYYY-MM-DD hh:mm') + ' [' + id + ']';
            const yValue = d[0].value;
            const yLabel = context.tooltip[id].y;
            const xValue = d[0].x;
            const xLabel = context.tooltip[id].x;

            return `<div class='c3-tooltip-container custom-tooltip'>
          <table class='c3-tooltip'>
            <tbody>
              <tr>
                <th colspan='2'>${title}</th>
              </tr>
              <tr>
                <td class='name'>
                  ${xLabel}
                </td>
                <td class='value'>
                  ${xValue}
                </td>
              </tr>
              <tr>
                <td class='name'>
                  ${yLabel}
                </td>
                <td class='value'>
                  ${yValue}
                </td>
              </tr>
            </tbody>
          </table>
        </div>`;
          }
        }
      };
      this.handler.$emit('init', options);
    }
  }
};

const filterByRange = (range, valueX, valueY, xDataArray, yDataArray) => {
  if (inRange(range.x, valueX)) {
    xDataArray.push(valueX);
  } else if (range.x.length === 0) {
    xDataArray.push(valueX);
  } else {
    xDataArray.push(null);
  }
  if (inRange(range.y, valueY)) {
    yDataArray.push(valueY);
  } else if (range.y.length === 0) {
    yDataArray.push(valueY);
  } else {
    yDataArray.push(null);
  }
};

const inRange = (range, value) => {
  return range && range.length === 2 && value >= range[0] && value <= range[1];
};

</script>
<style lang="scss">
  @import "../../../stylesheets/variables.scss";

  .correlation {
    display: inline-block;
  }
</style>



// WEBPACK FOOTER //
// src/components/explorer/correlation/ChartMultipleCorrelation.vue